<script setup>
import { computed } from 'vue'
import { formatNumber } from 'devextreme/localization'
import { DxTextBox } from 'devextreme-vue/text-box'
import { DxNumberBox } from 'devextreme-vue/number-box'

const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  },
  showSecondary: {
    type: Boolean,
    default: false
  },
  positionEditable: { type: Boolean, default: false },
  showSettings: { type: Boolean, default: true },
  showCurrentCost: { type: Boolean, default: true },
  changes: { type: Object, default: () => ({}) },
  missingAsZero: { type: Boolean, default: false },
})

const emit = defineEmits(['position-changed'])

const quantities = ['stock', 'ordered', 'customerCommitted', 'workshopCommitted', 'theoreticalStock']
const costs = computed(() => ['averageCost', 'weightedAverageCost', 'lastCost', ...(props.showCurrentCost ? ['currentCost'] : [])])

const formatValue = (value, format) => value == null && !props.missingAsZero ? '—' : formatNumber(value ?? 0, format)
</script>

<template>
  <div class="row q-col-gutter-md">
    <section class="col-12 col-md-6">
      <div class="text-weight-bold text-primary q-pb-sm">
        {{ $t('common.quantity') }}
      </div>
      <template
        v-for="field in quantities"
        :key="field"
      >
        <div class="row items-center q-py-sm">
          <span class="col">{{ $t(`stockInfo.${field}`) }}</span>
          <span
            v-for="suffix in props.showSecondary ? ['', '_secondary'] : ['']"
            :key="suffix"
            class="col-auto text-right text-weight-medium q-pl-sm"
            :class="{ 'text-grey-7': suffix }"
          >
            {{ formatValue(props.data[`${field}${suffix}`], '#,##0.###') }}
            <span
              v-if="props.changes[`${field}${suffix}`]"
              class="q-ml-xs"
              :class="props.changes[`${field}${suffix}`] > 0 ? 'text-positive' : 'text-negative'"
            >
              {{ props.changes[`${field}${suffix}`] > 0 ? '+' : '−' }}{{ formatNumber(Math.abs(props.changes[`${field}${suffix}`]), '#,##0.###') }}
            </span>
          </span>
        </div>
        <q-separator />
      </template>
    </section>
    <section class="col-12 col-md-6">
      <div class="text-weight-bold text-primary q-pb-sm">
        {{ $t('stockInfo.values') }}
      </div>
      <template
        v-for="field in costs"
        :key="field"
      >
        <div class="row items-center q-py-sm">
          <span class="col">{{ $t(`stockInfo.${field}`) }}</span>
          <span class="col-3 text-right text-weight-medium">{{ formatValue(props.data[field], '#,##0.00') }}</span>
        </div>
        <q-separator />
      </template>
    </section>
    <template v-if="props.showSettings">
      <div class="col-12 q-mt-md">
        <dx-text-box
          :value="props.data.position ?? ''"
          :label="$t('stockInfo.position')"
          label-mode="outside"
          :read-only="!props.positionEditable"
          :max-length="20"
          @value-changed="e => { if (e.event) emit('position-changed', e.value) }"
        />
      </div>
      <div class="col-12 col-md-6">
        <dx-number-box
          :value="props.data.minimum_stock ?? null"
          :label="$t('stockInfo.minimumStock')"
          label-mode="outside"
          :read-only="true"
        />
      </div>
      <div class="col-12 col-md-6">
        <dx-number-box
          :value="props.data.maximum_stock ?? null"
          :label="$t('stockInfo.maximumStock')"
          label-mode="outside"
          :read-only="true"
        />
      </div>
    </template>
  </div>
</template>

